import { useEffect, useState } from 'react';
import { isEmailIdentifier, type ISsoDiscovery } from '@d-orca/shared/client/utils';
import { discoverSso } from '@apis/sso';

/** Wait for the admin to stop typing — every keystroke is not a question worth asking. */
export const SSO_DISCOVERY_DEBOUNCE_MS = 400;

/**
 * Whether the address being typed signs in with its company account, for the
 * add-member dialog's read-only badge. `null` until a complete address has
 * settled (and while `enabled` is false); the backend caches per domain.
 */
export function useSsoDiscovery(email: string, enabled: boolean): ISsoDiscovery | null {
  const [result, setResult] = useState<{ email: string; discovery: ISsoDiscovery } | null>(null);
  const target = email.trim();
  const ask = enabled && isEmailIdentifier(target);

  useEffect(() => {
    if (!ask) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      void discoverSso(target).then((discovery) => {
        if (!cancelled) setResult({ email: target, discovery });
      });
    }, SSO_DISCOVERY_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ask, target]);

  // An answer about a previous address is no answer about this one.
  return ask && result?.email === target ? result.discovery : null;
}
